/**
 * Service / work-type Autocomplete with per-user MRU and a Most used group (#2672).
 * While the user is typing, query relevance wins and the list is not reshuffled by MRU.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import {
  Autocomplete,
  Box,
  CircularProgress,
  createFilterOptions,
  TextField,
  Typography,
} from '@mui/material';
import {
  dropdownGroupLabel,
  DROPDOWN_MRU_SELECTORS,
  orderDropdownOptions,
  pickMostUsedIds,
  useDropdownMru,
} from '@/hooks/useDropdownMru';
import type { Service } from '@/hooks/useServices';

const defaultFilter = createFilterOptions<Service>({ stringify: (option) => option.name });

/**
 * The "create a new work type" row (#4129). It is a real option, appended to
 * the list, so keyboard users reach it with the arrow keys like any service,
 * and it is still there when the search matches nothing. `name` is the text
 * typed when the row was built, so picking it hands the query to `onCreate`.
 */
interface CreateServiceOption {
  readonly isCreateOption: true;
  readonly id: '__create_service__';
  readonly name: string;
}

type CatalogOption = Service | CreateServiceOption;

function isCreateOption(option: CatalogOption): option is CreateServiceOption {
  return (option as Partial<CreateServiceOption>).isCreateOption === true;
}

export interface ServiceCatalogSelectProps {
  options: Service[];
  loading?: boolean;
  value?: Service | null;
  onChange: (service: Service | null) => void;
  label: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  autoFocus?: boolean;
  openOnFocus?: boolean;
  inputValue?: string;
  onInputChange?: (value: string, reason: string) => void;
  /**
   * When given, the list ends with "Create new work type" (also for an empty
   * search). Picking it calls this with the typed text; it selects nothing, the
   * caller opens its creation flow. Leave it out when the user may not create
   * catalog services — the row is then not offered at all.
   */
  onCreate?: (query: string) => void;
}

export function ServiceCatalogSelect({
  options,
  loading = false,
  value = null,
  onChange,
  label,
  placeholder,
  size = 'medium',
  autoFocus = false,
  openOnFocus = false,
  inputValue,
  onInputChange,
  onCreate,
}: ServiceCatalogSelectProps) {
  const { t } = useTranslation('workorders');
  // The creation text belongs to the catalog settings, next to the dialog it opens.
  const { t: ts } = useTranslation('settings');
  const mru = useDropdownMru(DROPDOWN_MRU_SELECTORS.service);
  const [uncontrolledInput, setUncontrolledInput] = useState('');
  const query = inputValue !== undefined ? inputValue : uncontrolledInput;
  const mostUsedLabel = t('dropdown.mostUsed');

  const mostUsedIds = useMemo(
    () => pickMostUsedIds(
      options,
      (option) => option.id,
      mru.recentIds,
      (option) => option.usageCount ?? 0,
    ),
    [options, mru.recentIds],
  );

  const grouped = query.trim() === '' && mostUsedIds.size > 0;

  return (
    <Autocomplete<CatalogOption>
      options={options}
      getOptionLabel={(option) => (isCreateOption(option) ? '' : option.name)}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      loading={loading}
      value={value}
      onChange={(_event, next) => {
        if (next && isCreateOption(next)) {
          onCreate?.(next.name);
          return;
        }
        onChange(next);
        if (next) mru.record(next.id);
      }}
      {...(inputValue !== undefined ? { inputValue } : {})}
      onInputChange={(_event, next, reason) => {
        const nextTyped = reason === 'reset' || reason === 'selectOption' ? '' : next;
        if (inputValue === undefined) setUncontrolledInput(nextTyped);
        onInputChange?.(next, reason);
      }}
      size={size}
      openOnFocus={openOnFocus}
      filterOptions={(opts, state) => {
        const services = opts.filter((option): option is Service => !isCreateOption(option));
        const filtered = defaultFilter(services, {
          inputValue: state.inputValue,
          getOptionLabel: (option) => option.name,
        });
        const ordered: CatalogOption[] = orderDropdownOptions(filtered, {
          query: state.inputValue,
          recentIds: mru.recentIds,
          getId: (option) => option.id,
          getLabel: (option) => option.name,
          usageCount: (option) => option.usageCount ?? 0,
        });
        if (onCreate) {
          ordered.push({ isCreateOption: true, id: '__create_service__', name: state.inputValue.trim() });
        }
        return ordered;
      }}
      groupBy={grouped
        ? (option) => isCreateOption(option) ? '' : dropdownGroupLabel(option, {
            query,
            mostUsedIds,
            getId: (item) => item.id,
            mostUsedLabel,
          })
        : undefined}
      renderGroup={grouped
        ? (params) => (
            <li key={params.key}>
              {params.group !== '' && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', px: 2, py: 0.5 }}
                >
                  {params.group}
                </Typography>
              )}
              <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
            </li>
          )
        : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          autoFocus={autoFocus}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => isCreateOption(option) ? (
        <li
          {...props}
          key={option.id}
          data-testid="service-create-option"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
            <AddIcon fontSize="small" />
            <Typography variant="body2" color="inherit">
              {option.name === ''
                ? ts('servicesTab.createFromPicker')
                : ts('servicesTab.createFromPickerNamed', { name: option.name })}
            </Typography>
          </Box>
        </li>
      ) : (
        <li {...props} key={option.id}>
          <Box>
            <Typography variant="body2">{option.name}</Typography>
            {option.description && (
              <Typography variant="caption" color="text.secondary">
                {option.description}
              </Typography>
            )}
          </Box>
        </li>
      )}
    />
  );
}
